import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Plus, HandCoins, Users, BarChart3, Flame, DollarSign, Activity,
  Zap, Trophy, MapPin, Bookmark, TrendingUp, X
} from "lucide-react";
import { BACKEND_URL } from "./networkConstants";
import { getPropertyPhoto, fmt, computeDealScore } from "./networkUtils";
import { AuthorAvatar, DealScoreBadge } from "./NetworkShared";

// ─── Quick Actions ─────────────────────────────────────────────────────────────
export function QuickActionsPanel({ onStartPost }) {
  const navigate = useNavigate();
  const actions = [
    { icon: Plus,      label: "Post Deal",     color: "text-[#D4AF37]",   bg: "bg-[#D4AF37]/10 border-[#D4AF37]/20",   fn: () => navigate("/deals")                    },
    { icon: HandCoins, label: "Raise Capital", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20",fn: () => onStartPost("funding_update")         },
    { icon: Users,     label: "Find Partner",  color: "text-blue-400",    bg: "bg-blue-500/10 border-blue-500/20",      fn: () => onStartPost("partner_request")        },
    { icon: BarChart3, label: "Analyze Deal",  color: "text-purple-400",  bg: "bg-purple-500/10 border-purple-500/20",  fn: () => navigate("/deals")                    },
  ];
  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4">
      <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold mb-3">Quick Actions</p>
      <div className="space-y-2">
        {actions.map(a => {
          const Icon = a.icon;
          return (
            <button key={a.label} onClick={a.fn}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border ${a.bg} transition-all hover:scale-[1.02] text-left`}>
              <Icon className={`w-3.5 h-3.5 ${a.color} flex-shrink-0`} />
              <span className={`text-xs font-medium ${a.color}`}>{a.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Hot Deals Widget ──────────────────────────────────────────────────────────
export function HotDealsWidget({ onDealClick }) {
  const [deals, setDeals] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/deals/network-board`, { withCredentials: true })
      .then(r => setDeals((r.data?.deals || []).sort((a, b) => (b.marketplace_score || 0) - (a.marketplace_score || 0)).slice(0, 4)))
      .catch(() => {});
  }, []);
  if (!deals.length) return null;
  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Flame className="w-3.5 h-3.5 text-orange-400" />
        <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold">Trending Now</p>
      </div>
      <div className="space-y-2">
        {deals.map((d, i) => {
          const photo = getPropertyPhoto(d.property_type);
          const score = d.marketplace_score || 0;
          return (
            <button key={d.deal_id} onClick={() => onDealClick(d)}
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-white/[0.03] transition-all text-left group">
              <span className={`text-xs font-black w-4 flex-shrink-0 ${i === 0 ? 'text-[#D4AF37]' : 'text-white/30'}`}>#{i+1}</span>
              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                <img src={photo} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" alt="" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/80 text-xs font-medium truncate">{d.property_address?.split(",")[0]}</p>
                <p className="text-white/30 text-[10px]">{d.property_type || "Property"}</p>
              </div>
              {score > 0 && (
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md ${score >= 80 ? 'text-orange-400 bg-orange-500/10' : 'text-white/40 bg-white/[0.04]'}`}>
                  {score}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Capital Ready Widget ──────────────────────────────────────────────────────
export function CapitalReadyWidget() {
  const [capital, setCapital] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/network/capital-ready`, { withCredentials: true })
      .then(r => setCapital(r.data || []))
      .catch(() => {});
  }, []);
  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <HandCoins className="w-3.5 h-3.5 text-emerald-400" />
        <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold">Capital Ready</p>
      </div>
      {capital.length === 0 ? (
        <div className="space-y-2">
          {[
            { name: "Private Lender", amount: "$250K", detail: "10% · 70% LTV · Flips", color: "text-emerald-400" },
            { name: "Hard Money Co.", amount: "$500K", detail: "12% · 65% LTV · DSCR",  color: "text-[#D4AF37]"  },
            { name: "Equity Partner",  amount: "$100K", detail: "Profit share · TN",     color: "text-blue-400"   },
          ].map((c, i) => (
            <div key={i} className="flex items-center gap-3 p-2 rounded-xl bg-white/[0.02] border border-white/[0.04]">
              <div className="w-7 h-7 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-3 h-3 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/70 text-xs font-medium truncate">{c.name}</p>
                <p className="text-white/30 text-[10px]">{c.detail}</p>
              </div>
              <span className={`text-xs font-bold ${c.color}`}>{c.amount}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {capital.map((c, i) => (
            <div key={i} className="flex items-center gap-3 p-2 rounded-xl bg-white/[0.02] border border-white/[0.04]">
              <div className="w-7 h-7 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-3 h-3 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white/70 text-xs font-medium truncate">{c.name}</p>
                <p className="text-white/30 text-[10px]">{c.detail}</p>
              </div>
              <span className="text-xs font-bold text-emerald-400">{c.amount_str}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Deal Insights Widget ──────────────────────────────────────────────────────
export function DealInsightsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/network/deal-insights`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4 animate-pulse">
      <div className="h-3 w-32 bg-white/[0.06] rounded mb-3" />
      <div className="space-y-2">
        <div className="h-8 bg-white/[0.04] rounded-lg" />
        <div className="h-8 bg-white/[0.04] rounded-lg" />
      </div>
    </div>
  );

  if (!data) return null;

  const { stats, ai_insight } = data;
  const fmtVal = (v) => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${Math.round(v/1000)}K` : `$${v}`;

  const statItems = [
    { label: "Avg ARV",    value: stats.avg_arv   > 0 ? fmtVal(stats.avg_arv)   : "—", color: "text-[#00FF88]"  },
    { label: "Avg Rehab",  value: stats.avg_rehab  > 0 ? fmtVal(stats.avg_rehab)  : "—", color: "text-orange-400" },
    { label: "Avg Spread", value: stats.avg_spread > 0 ? fmtVal(stats.avg_spread) : "—", color: "text-blue-400"   },
    { label: "Live Deals", value: stats.active_deals || "0",                              color: "text-[#D4AF37]"  },
  ].filter(s => s.value !== "—" && s.value !== "0");

  return (
    <div className="bg-[#0D0D0D] border border-[#D4AF37]/10 rounded-2xl p-4" data-testid="deal-insights-widget">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-3.5 h-3.5 text-[#D4AF37]" />
        <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold">Deal Insights</p>
        <span className="ml-auto text-[8px] text-white/20">{stats.deals_this_week > 0 ? `+${stats.deals_this_week} this wk` : ""}</span>
      </div>

      {statItems.length > 0 && (
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {statItems.map((s, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/[0.05] rounded-xl p-2.5 text-center">
              <p className={`text-sm font-black ${s.color}`}>{s.value}</p>
              <p className="text-[9px] text-white/25 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mb-3">
        {stats.top_strategy && (
          <span className="text-[9px] text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/20 px-2 py-0.5 rounded-full font-medium">
            #{stats.top_strategy.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase())}
          </span>
        )}
        {stats.top_location && (
          <span className="text-[9px] text-white/50 bg-white/[0.04] border border-white/[0.08] px-2 py-0.5 rounded-full flex items-center gap-1">
            <MapPin className="w-2 h-2" />{stats.top_location}
          </span>
        )}
      </div>

      {ai_insight && (
        <div className="border-t border-white/[0.05] pt-3">
          <div className="flex items-start gap-2">
            <div className="w-4 h-4 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Zap className="w-2 h-2 text-[#D4AF37]" />
            </div>
            <p className="text-white/50 text-[11px] leading-relaxed">{ai_insight}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Leaderboard Widget ────────────────────────────────────────────────────────
export function LeaderboardWidget() {
  const [leaders, setLeaders] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/network/leaderboard`, { withCredentials: true })
      .then(r => setLeaders(r.data || []))
      .catch(() => {});
  }, []);
  const medals = ["🥇", "🥈", "🥉"];
  const roleColors = { investor: "text-[#D4AF37]", lender: "text-emerald-400", wholesaler: "text-blue-400", contractor: "text-orange-400" };
  if (!leaders.length) return null;
  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-3.5 h-3.5 text-[#D4AF37]" />
        <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold">Weekly Leaderboard</p>
      </div>
      <div className="space-y-2">
        {leaders.slice(0, 5).map((l, i) => (
          <div key={l.user_id} className="flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-white/[0.02] transition-all">
            <span className="text-sm w-5 text-center flex-shrink-0">{medals[i] || `#${i+1}`}</span>
            <AuthorAvatar picture={l.picture} name={l.name} role={l.role} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-white/80 text-xs font-semibold truncate">{l.name}</p>
              <p className={`text-[10px] ${roleColors[l.role] || "text-white/30"}`}>{l.deals} deals · {l.posts} posts</p>
            </div>
            <span className="text-[10px] font-black text-[#D4AF37]">{l.score}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Live Activity Widget ──────────────────────────────────────────────────────
export function LiveActivityWidget() {
  const [activities, setActivities] = useState([]);
  useEffect(() => {
    const fetch = () => {
      axios.get(`${BACKEND_URL}/api/network/activity`, { withCredentials: true })
        .then(r => setActivities(r.data || []))
        .catch(() => {});
    };
    fetch();
    const id = setInterval(fetch, 30000);
    return () => clearInterval(id);
  }, []);
  if (!activities.length) return null;
  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-3.5 h-3.5 text-blue-400" />
        <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold">Live Activity</p>
        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse" />
      </div>
      <div className="space-y-2">
        {activities.slice(0, 7).map((a, i) => (
          <div key={i} className="flex items-start gap-2 animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <span className="text-sm flex-shrink-0 mt-0.5">{a.icon}</span>
            <p className="text-white/50 text-[11px] leading-snug line-clamp-2">{a.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Deal Score Leaderboard ────────────────────────────────────────────────────
export function DealScoreLeaderboardWidget({ onDealClick }) {
  const [deals, setDeals] = useState([]);
  const [weekCount, setWeekCount] = useState(0);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/deals/network-board`, { withCredentials: true })
      .then(r => {
        const all = r.data?.deals || [];
        const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        setWeekCount(all.filter(d => new Date(d.created_at) >= oneWeekAgo).length);
        // use computed score as fallback if marketplace_score not set
        const withScores = all.map(d => ({
          ...d,
          _score: d.marketplace_score || computeDealScore(d.arv, d.purchase_price, d.rehab_estimate) || 0
        }));
        setDeals(withScores.sort((a, b) => b._score - a._score).slice(0, 5));
      })
      .catch(() => {});
  }, []);

  const scoreColor = (s) => s >= 90 ? "text-[#00FF88]" : s >= 75 ? "text-blue-400" : s >= 60 ? "text-yellow-400" : "text-white/40";
  const scoreLabel = (s) => s >= 90 ? "Elite"          : s >= 75 ? "Strong"        : s >= 60 ? "Avg"           : "Risk";
  const medals     = ["🥇", "🥈", "🥉"];

  if (!deals.length) return null;

  return (
    <div className="bg-[#0D0D0D] border border-[#D4AF37]/15 rounded-2xl p-4" data-testid="deal-score-leaderboard">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-[#D4AF37]" />
          <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold">Deal Score Board</p>
        </div>
        {weekCount > 0 && (
          <span className="text-[9px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-full font-bold">
            +{weekCount} this wk
          </span>
        )}
      </div>

      <div className="space-y-1">
        {deals.map((d, i) => {
          const score  = d._score || 0;
          const arv    = d.arv || 0;
          const allIn  = (d.purchase_price || 0) + (d.rehab_estimate || 0);
          // only show ROI when purchase_price is explicitly set (avoid inflated numbers)
          const roi    = d.purchase_price > 0 && arv > 0 && allIn > 0
            ? Math.round(((arv - allIn) / allIn) * 100)
            : null;
          const strat  = (d.marketplace_strategy || d.deal_type || "").replace(/_/g, " ");
          return (
            <button key={d.deal_id} onClick={() => onDealClick?.(d)}
              className="w-full flex items-center gap-2.5 p-2.5 rounded-xl hover:bg-white/[0.03] transition-all text-left group">
              <span className="text-sm w-5 text-center flex-shrink-0 leading-none">
                {medals[i] || <span className="text-[10px] text-white/20 font-bold">#{i+1}</span>}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white/80 text-xs font-semibold truncate leading-tight">
                  {(d.property_address || "").split(",")[0]}
                </p>
                <p className="text-white/30 text-[10px] capitalize truncate">
                  {strat || d.property_type || "Property"}
                  {roi != null && ` · ${roi > 0 ? "+" : ""}${roi}% ROI`}
                </p>
              </div>
              <div className="flex flex-col items-end flex-shrink-0">
                <span className={`text-sm font-black leading-none ${scoreColor(score)}`}>{score}</span>
                <span className={`text-[8px] font-bold opacity-60 ${scoreColor(score)}`}>{scoreLabel(score)}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 pt-3 border-t border-white/[0.04] text-center">
        <p className="text-[9px] text-white/15">Score 0–100 · ROI + Equity + Market signals</p>
      </div>
    </div>
  );
}

// ─── Watchlist Widget ──────────────────────────────────────────────────────────
export function WatchlistWidget({ onDealClick }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    axios.get(`${BACKEND_URL}/api/network/watchlist`, { withCredentials: true })
      .then(r => setDeals(r.data?.deals || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (e, dealId) => {
    e.stopPropagation();
    await axios.delete(`${BACKEND_URL}/api/network/watchlist/${dealId}`, { withCredentials: true }).catch(() => {});
    setDeals(d => d.filter(x => x.deal_id !== dealId));
  };

  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-2xl p-4" data-testid="watchlist-widget">
      <div className="flex items-center gap-2 mb-3">
        <Bookmark className="w-3.5 h-3.5 text-[#D4AF37]" />
        <p className="text-[9px] text-white/30 uppercase tracking-widest font-bold">My Watchlist</p>
        {deals.length > 0 && (
          <span className="ml-auto text-[9px] text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/20 px-1.5 py-0.5 rounded-full font-bold">{deals.length}</span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2].map(i => <div key={i} className="h-10 rounded-xl bg-white/[0.03] animate-pulse" />)}
        </div>
      ) : deals.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-white/20 text-xs leading-relaxed">Bookmark deals from the<br />Deal Board to track them here</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {deals.map(d => {
            const arv = d.arv || 0;
            const score = d.marketplace_score || computeDealScore(d.arv, d.purchase_price, d.rehab_estimate) || 0;
            const scoreColor = score >= 90 ? "text-[#00FF88]" : score >= 75 ? "text-blue-400" : score >= 60 ? "text-yellow-400" : "text-white/30";
            return (
              <div key={d.deal_id} className="flex items-center gap-2 p-2 rounded-xl hover:bg-white/[0.03] transition-all group/item cursor-pointer"
                onClick={() => onDealClick?.(d)}>
                <div className="flex-1 min-w-0">
                  <p className="text-white/70 text-xs font-medium truncate">{(d.property_address || "").split(",")[0]}</p>
                  <p className="text-white/25 text-[10px]">{arv >= 1000 ? `ARV ${fmt(arv)}` : d.property_type || "Property"}</p>
                </div>
                {score > 0 && <span className={`text-[10px] font-black flex-shrink-0 ${scoreColor}`}>{score}</span>}
                <button onClick={e => remove(e, d.deal_id)} title="Remove"
                  data-testid={`remove-watchlist-${d.deal_id}`}
                  className="opacity-0 group-hover/item:opacity-100 p-1 text-white/20 hover:text-red-400 transition-all flex-shrink-0">
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
